import { FC, useState } from 'react';
import {
  Button,
  ModalIcon,
  Stack,
  TextBody,
  TextTitle,
} from '@pypestream/design-system';
import { TranslationComponent } from '@pypestream/translations';

import { GenericModal, GenericModalProps } from '..';
import { sendProStudioEvent } from '../../../xstate/app.xstate';
import { Channel } from '../../../utils';

export type DeleteChannelModalProps = GenericModalProps & {
  channel: Channel | null;
};

export const DeleteChannelModal: FC<DeleteChannelModalProps> = ({
  channel,
  modalRef,
  onClose: onCloseProp,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const onClose = () => {
    onCloseProp?.();
    setIsLoading(false);
  };

  const handleDelete = () => {
    if (!channel) {
      return;
    }

    const { id, pypeId, streamId, env } = channel;

    setIsLoading(true);

    sendProStudioEvent({
      type: 'deleteChannel',
      payload: {
        id,
        pypeId,
        streamId,
        env,
        cb: () => {
          onClose();
        },
      },
    });
  };

  return (
    <GenericModal
      test-id="delete-widget-modal"
      modalRef={modalRef}
      onClose={onClose}
      onError={() => setIsLoading(false)}
      hideCloseBtn={isLoading}
      stayOnClickOutside={isLoading}
    >
      <ModalIcon
        name="error"
        slot="header"
        style={{ display: 'flex', justifyContent: 'center' }}
      />
      <Stack slot="header" alignItems="center" direction="column">
        <TextTitle size="small">
          <TranslationComponent i18nKey="pro-studio/channels:deleteChannelModal.title">
            Are you sure?
          </TranslationComponent>
        </TextTitle>
        <TextBody variant="secondary">
          <TranslationComponent i18nKey="pro-studio/channels:deleteChannelModal.description">
            Are you sure you want to delete this channel? Users will no longer
            be able to access your solution using it.
          </TranslationComponent>
        </TextBody>
      </Stack>
      <Stack slot="footer" justifyContent="end">
        <Button
          test-id="cancel-delete-widget"
          variant="ghost"
          size="large"
          onClick={onClose}
          disabled={isLoading}
        >
          <TranslationComponent i18nKey="pro-studio/channels:deleteChannelModal.cancel">
            Cancel
          </TranslationComponent>
        </Button>
        <Button
          test-id="confirm-delete-widget"
          size="large"
          variant="warning"
          onClick={handleDelete}
          disabled={isLoading}
        >
          <TranslationComponent i18nKey="pro-studio/channels:deleteChannelModal.delete">
            Delete Channel
          </TranslationComponent>
        </Button>
      </Stack>
    </GenericModal>
  );
};
